import React, { useState, useEffect } from 'react';
import './GameInfo.css';

const GameInfo = ({ gameState, gameData, currentPlayer, timeLeft, turnTimeLeft, onBackToMenu, onBackToSetup }) => {
  const [availableImages, setAvailableImages] = useState({});
  const [showRules, setShowRules] = useState(false);

  const pieceSymbols = {
    white: {
      'king': '♔',
      'knight': '♘',
      'pawn': '♙',
      'advisor': '📚',
      'cannon': '🔫',
      'camel': '🐪',
      'donkey': '🐴',
      'ram': '♖',
      'spy': '⚔️'
    },
    black: {
      'king': '♚',
      'knight': '♞',
      'pawn': '♟',
      'advisor': '📚',
      'cannon': '🔫',
      'camel': '🐪',
      'donkey': '🐴',
      'ram': '♜',
      'spy': '⚔️'
    }
  };

  const PIECE_IMAGES = {
    white: {
      'king': '/assets/pieces/white-king.png',
      'knight': '/assets/pieces/white-knight.png',
      'pawn': '/assets/pieces/white-pawn.png',
      'advisor': '/assets/pieces/white-advisor.png',
      'cannon': '/assets/pieces/white-cannon.png',
      'camel': '/assets/pieces/white-camel.png',
      'donkey': '/assets/pieces/white-donkey.png',
      'ram': '/assets/pieces/white-ram.png',
      'spy': '/assets/pieces/white-spy.png'
    },
    black: {
      'king': '/assets/pieces/black-king.png',
      'knight': '/assets/pieces/black-knight.png',
      'pawn': '/assets/pieces/black-pawn.png',
      'advisor': '/assets/pieces/black-advisor.png',
      'cannon': '/assets/pieces/black-cannon.png',
      'camel': '/assets/pieces/black-camel.png',
      'donkey': '/assets/pieces/black-donkey.png',
      'ram': '/assets/pieces/black-ram.png',
      'spy': '/assets/pieces/black-spy.png'
    }
  };

  // Check which PNG images are available
  useEffect(() => {
    const checkImageAvailability = async () => {
      const imageChecks = {};
      
      // Check all piece types for both colors
      for (const color of ['white', 'black']) {
        for (const pieceType of ['king', 'knight', 'pawn', 'advisor', 'cannon', 'camel', 'donkey', 'ram', 'spy']) {
          const key = `${color}-${pieceType}`;
          try {
            const img = new Image();
            img.src = PIECE_IMAGES[color][pieceType];
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });
            imageChecks[key] = true;
          } catch (error) {
            imageChecks[key] = false;
          }
        }
      }
      
      setAvailableImages(imageChecks);
    };
    checkImageAvailability();
  }, []);

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return "∞";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const renderPiece = (piece, size = '1.2rem') => {
    const imageKey = `${piece.color}-${piece.type}`;
    
    if (availableImages[imageKey]) {
      return (
        <img 
          src={PIECE_IMAGES[piece.color][piece.type]} 
          alt={`${piece.color} ${piece.type}`} 
          style={{ width: size, height: size }}
        />
      );
    } else {
      return (
        <span style={{ fontSize: size }}>
          {pieceSymbols[piece.color][piece.type]}
        </span>
      );
    }
  };

  const renderCapturedPieces = (color) => {
    if (!gameState || !gameState.capturedPieces) return null;
    
    const pieces = gameState.capturedPieces[color] || [];
    
    return (
      <div className={`captured-pieces ${color}`}>
        <h4>Captured {color} pieces:</h4>
        <div className="captured-list">
          {pieces.map((piece, index) => (
            <span key={index} className="captured-piece">
              {renderPiece(piece)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderTimeControls = () => {
    // Get the original time controls from gameData
    const originalTimeControls = gameData?.timeControls;
    if (!originalTimeControls) return null;

    // Check which timers are not unlimited (not null)
    const hasHumanThinking = originalTimeControls.humanThinkingTime !== null;
    const hasAiThinking = originalTimeControls.aiThinkingTime !== null;
    const hasHumanTotal = originalTimeControls.humanTotalTime !== null;
    const hasAiTotal = originalTimeControls.aiTotalTime !== null;

    // Only show if at least one timer is not unlimited
    if (!hasHumanThinking && !hasAiThinking && !hasHumanTotal && !hasAiTotal) return null;

    // Determine player types
    const isHumanWhite = gameData?.whitePlayerName && !gameData.whitePlayerName.includes('AI');
    const isHumanBlack = gameData?.blackPlayerName && !gameData.blackPlayerName.includes('AI');

    return (
      <div className="time-controls-info">
        <h3>⏱️ Time Controls</h3>
        
        <div className="time-controls-grid">
          {/* Thinking Times Row */}
          {(hasHumanThinking || hasAiThinking) && (
            <div className="thinking-times-row">
              {/* Human Thinking Time */}
              {hasHumanThinking && (
                <div className="time-control-item">
                  <div className="time-control-label">
                    <span className="player-icon">👤</span>
                    Human Thinking
                  </div>
                  <div className="time-control-value">
                    {isHumanWhite ? (
                      <span className={`time-value ${turnTimeLeft?.white <= 30 ? 'low-time' : ''}`}>
                        {formatTime(turnTimeLeft?.white || originalTimeControls.humanThinkingTime)}
                      </span>
                    ) : (
                      <span className={`time-value ${turnTimeLeft?.black <= 30 ? 'low-time' : ''}`}>
                        {formatTime(turnTimeLeft?.black || originalTimeControls.humanThinkingTime)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* AI Thinking Time */}
              {hasAiThinking && (
                <div className="time-control-item">
                  <div className="time-control-label">
                    <span className="player-icon">🤖</span>
                    AI Thinking
                  </div>
                  <div className="time-control-value">
                    {!isHumanWhite ? (
                      <span className={`time-value ${turnTimeLeft?.white <= 30 ? 'low-time' : ''}`}>
                        {formatTime(turnTimeLeft?.white || originalTimeControls.aiThinkingTime)}
                      </span>
                    ) : (
                      <span className={`time-value ${turnTimeLeft?.black <= 30 ? 'low-time' : ''}`}>
                        {formatTime(turnTimeLeft?.black || originalTimeControls.aiThinkingTime)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Total Times Row */}
          {(hasHumanTotal || hasAiTotal) && (
            <div className="total-times-row">
              {/* Human Total Time */}
              {hasHumanTotal && (
                <div className="time-control-item">
                  <div className="time-control-label">
                    <span className="player-icon">👤</span>
                    Human Total Time
                  </div>
                  <div className="time-control-value">
                    {isHumanWhite ? (
                      <span className={`time-value ${timeLeft?.white <= 60 ? 'low-time' : ''}`}>
                        {formatTime(timeLeft?.white || originalTimeControls.humanTotalTime)}
                      </span>
                    ) : (
                      <span className={`time-value ${timeLeft?.black <= 60 ? 'low-time' : ''}`}>
                        {formatTime(timeLeft?.black || originalTimeControls.humanTotalTime)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* AI Total Time */}
              {hasAiTotal && (
                <div className="time-control-item">
                  <div className="time-control-label">
                    <span className="player-icon">🤖</span>
                    AI Total Time
                  </div>
                  <div className="time-control-value">
                    {!isHumanWhite ? (
                      <span className={`time-value ${timeLeft?.white <= 60 ? 'low-time' : ''}`}>
                        {formatTime(timeLeft?.white || originalTimeControls.aiTotalTime)}
                      </span>
                    ) : (
                      <span className={`time-value ${timeLeft?.black <= 60 ? 'low-time' : ''}`}>
                        {formatTime(timeLeft?.black || originalTimeControls.aiTotalTime)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPlayerTimings = () => {
    // Always show turn times regardless of time controls
    return (
      <div className="player-timings">
        <h3>⏱️ Move Times</h3>
        
        <div className="timings-grid">
          {/* White Times */}
          <div className="player-timing-section">
            <h4 className="player-timing-header white-header">
              White ({gameData?.whitePlayerName || 'Player'})
            </h4>
            <div className="timing-item">
              <span className="timing-label">Turn Time:</span>
              <span className="timing-value">
                {turnTimeLeft?.white !== null && turnTimeLeft?.white !== undefined 
                  ? formatTime(turnTimeLeft.white)
                  : '—'}
              </span>
            </div>
            <div className="timing-item">
              <span className="timing-label">Total Time:</span>
              <span className="timing-value">
                {timeLeft?.white !== null && timeLeft?.white !== undefined 
                  ? formatTime(timeLeft.white)
                  : '—'}
              </span>
            </div>
          </div>

          {/* Black Times */}
          <div className="player-timing-section">
            <h4 className="player-timing-header black-header">
              Black ({gameData?.blackPlayerName || 'Player'})
            </h4>
            <div className="timing-item">
              <span className="timing-label">Turn Time:</span>
              <span className="timing-value">
                {turnTimeLeft?.black !== null && turnTimeLeft?.black !== undefined 
                  ? formatTime(turnTimeLeft.black)
                  : '—'}
              </span>
            </div>
            <div className="timing-item">
              <span className="timing-label">Total Time:</span>
              <span className="timing-value">
                {timeLeft?.black !== null && timeLeft?.black !== undefined 
                  ? formatTime(timeLeft.black)
                  : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="game-info">
      {renderTimeControls()}
      {renderPlayerTimings()}
      
      {gameState && (
        <div className="game-status">
          <h3>Game Status</h3>
          {gameState.gameOver ? (
            <div className="game-over">
              <h2>{gameState.gameOverReason || 'Game Over!'}</h2>
              <div className="game-controls" style={{ marginTop: '15px' }}>
                <button 
                  onClick={onBackToSetup} 
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginRight: '10px'
                  }}
                >
                  New Game
                </button>
                <button 
                  onClick={onBackToMenu} 
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Back to Menu
                </button>
              </div>
            </div>
          ) : (
            <div className="game-active">
              <p>Current Player: <strong>
                {currentPlayer === 'white' ? gameData?.whitePlayerName : gameData?.blackPlayerName}
                {' '}<span className="player-color-small">({currentPlayer})</span>
              </strong></p>
              {gameState.frozenKings && gameState.frozenKings.length > 0 && (
                <div className="frozen-kings">
                  <h4>🧊 Frozen Kings:</h4>
                  {gameState.frozenKings.map(king => (
                    <p key={king}><strong>{king}</strong> king is frozen in a 3x3 area!</p>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {renderCapturedPieces('white')}
          {renderCapturedPieces('black')}
        </div>
      )}
      
      <div className="game-rules">
        <div className="rules-header" onClick={() => setShowRules(!showRules)}>
          <div className="rules-header-text">
            <h3>Game Rules</h3>
            <span className="rules-toggle-hint">
              Click to {showRules ? 'hide' : 'show'} rules
            </span>
          </div>
          <button 
            className="rules-toggle-button"
            aria-label={showRules ? 'Hide rules' : 'Show rules'}
          >
            {showRules ? '▼' : '▶'}
          </button>
        </div>
        {showRules && (
          <div className="rules-content">
            <ul>
              <li>
                <strong>King ({renderPiece({color: 'white', type: 'king'}, '1rem')}/{renderPiece({color: 'black', type: 'king'}, '1rem')}):</strong> Moves 1 square in any direction
              </li>
              <li>
                <strong>Knight ({renderPiece({color: 'white', type: 'knight'}, '1rem')}/{renderPiece({color: 'black', type: 'knight'}, '1rem')}):</strong> Moves in L-shape (2+1 squares)
              </li>
              <li>
                <strong>Pawn ({renderPiece({color: 'white', type: 'pawn'}, '1rem')}/{renderPiece({color: 'black', type: 'pawn'}, '1rem')}):</strong> Moves forward, captures diagonally
              </li>
              <li>
                <strong>Advisor ({renderPiece({color: 'white', type: 'advisor'}, '1rem')}/{renderPiece({color: 'black', type: 'advisor'}, '1rem')}):</strong> Moves 1-2 squares in any direction
              </li>
              <li>
                <strong>Cannon ({renderPiece({color: 'white', type: 'cannon'}, '1rem')}/{renderPiece({color: 'black', type: 'cannon'}, '1rem')}):</strong> Moves like rook, jumps to capture
              </li>
              <li>
                <strong>Camel ({renderPiece({color: 'white', type: 'camel'}, '1rem')}/{renderPiece({color: 'black', type: 'camel'}, '1rem')}):</strong> Moves in (3,1) pattern
              </li>
              <li>
                <strong>Donkey ({renderPiece({color: 'white', type: 'donkey'}, '1rem')}/{renderPiece({color: 'black', type: 'donkey'}, '1rem')}):</strong> 1 square any direction or 2 diagonal on same color
              </li>
              <li>
                <strong>Ram ({renderPiece({color: 'white', type: 'ram'}, '1rem')}/{renderPiece({color: 'black', type: 'ram'}, '1rem')}):</strong> Moves like rook, bumps pieces backward (captures if bumped off board)
              </li>
              <li>
                <strong>Spy ({renderPiece({color: 'white', type: 'spy'}, '1rem')}/{renderPiece({color: 'black', type: 'spy'}, '1rem')}):</strong> 1 square any direction or any number backwards
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameInfo; 